import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { CustomerService, TelephonyService, UserService } from '../../../services';
import { IProfileJobInfo } from '../../../interfaces/telephony/profile-job-info';
import { JobStatus } from '../../../enums/job-status';
import { IGetProfileOrdersFilter } from '../../../interfaces/telephony/customer/get-profile-orders-filter.interface';
import { Subscription } from 'rxjs';
import { ICustomerStructureData } from '../../../interfaces/telephony/customer/customer-structure-data.dto.interface';

export enum JobProfileTabEnum {
  liveJobs,
  unallocatedJobs,
  allocatedJobs,
  completedJobs,
  closedJobs,
  cancelledJobs,
}

interface JobFilter {
  deptId: number;
  teamId: number;
  userId: string;
}

@Component({
  templateUrl: './customer-information.component.html',
  styleUrls: ['./customer-information.component.scss'],
})
export class CustomerInformationComponent implements OnInit, OnDestroy {
  readonly JobProfileTab = JobProfileTabEnum;

  customerId: number;
  allJobs: IProfileJobInfo[] = [];
  jobsByStatus: { [key in JobProfileTabEnum]: IProfileJobInfo[] } = Object.values(
    JobProfileTabEnum,
  ).reduce(
    (acc, value) => ({ ...acc, [value]: [] }),
    {} as { [key in JobProfileTabEnum]: IProfileJobInfo[] },
  );

  isBusy = false;
  callerUserId: string;
  companyInfoURL: string;

  activeIndex = 0;

  isImpersonating: boolean = false;

  private routeSubscription: Subscription;

  constructor(
    private route: ActivatedRoute,
    private customerService: CustomerService,
    private telephonyService: TelephonyService,
    private userService: UserService,
  ) {}

  structureData: ICustomerStructureData;
  showImpersonateDialog: boolean;

  async ngOnInit() {
    this.activeIndex = 3;

    this.routeSubscription = this.route.params.subscribe((params: Params) => {
      this.customerId = params['id'];

      this.loadCustomerInfoURL();
      void this.loadCustomerOrders();
      this.loadStructureData();

      this.activeIndex = 3;
    });

    this.telephonyService.GetCurrentCallState().then((res) => {
      this.callerUserId = res?.callerInfo?.userId;
    });

    this.isImpersonating = (await this.userService.isUserImpersonating()).isImpersonating;
  }

  ngOnDestroy() {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  loadStructureData() {
    this.isBusy = true;

    this.customerService
      .getStructureData(this.customerId)
      .then((res) => {
        this.structureData = res;

        this.isBusy = false;
      })
      .catch(() => {
        this.isBusy = false;
      });
  }

  loadCustomerInfoURL() {
    this.customerService.getCompanyInfoURL(this.customerId).then((res) => {
      //console.log('** Company Info URL: ', res);
      this.companyInfoURL = res.url;
    });
  }

  async loadCustomerOrders() {
    this.isBusy = true;
    try {
      const filter: IGetProfileOrdersFilter = {
        customerId: this.customerId,
        searchReg: null,
      };

      this.allJobs = await this.customerService.getProfileOrders(filter);

      this.filterJobs();
    } finally {
      this.isBusy = false;
    }
  }

  onOrdersFilterChanged(filter: JobFilter, tab: JobProfileTabEnum) {
    this.filterJobs(filter, tab);
  }

  private filterJobs(filter?: JobFilter, tab?: JobProfileTabEnum) {
    const applyFilter = (jobs: IProfileJobInfo[]) =>
      filter
        ? jobs.filter(
            (job) =>
              (!filter.deptId || job.deptId === filter.deptId) &&
              (!filter.teamId || job.teamId === filter.teamId) &&
              (!filter.userId || job.orderedByUserId === filter.userId),
          )
        : jobs;

    const statusMap: { [key in JobProfileTabEnum]: JobStatus } = {
      [JobProfileTabEnum.liveJobs]: JobStatus.Started,
      [JobProfileTabEnum.unallocatedJobs]: JobStatus.Created,
      [JobProfileTabEnum.allocatedJobs]: JobStatus.Created,
      [JobProfileTabEnum.completedJobs]: JobStatus.Completed,
      [JobProfileTabEnum.closedJobs]: JobStatus.Closed,
      [JobProfileTabEnum.cancelledJobs]: JobStatus.Cancelled,
    };

    const tabsToUpdate = tab ? [tab] : Object.values(JobProfileTabEnum);

    tabsToUpdate.forEach((currentTab) => {
      const status = statusMap[currentTab];
      let filteredJobs = this.allJobs.filter((job) => job.statusId === status);

      if (currentTab === JobProfileTabEnum.unallocatedJobs) {
        filteredJobs = filteredJobs.filter((job) => !job.supplierId);
      } else if (currentTab === JobProfileTabEnum.allocatedJobs) {
        filteredJobs = filteredJobs.filter((job) => !!job.supplierId);
      }

      this.jobsByStatus[currentTab] = applyFilter(filteredJobs);
    });
  }

  gotoCustomerInformation() {
    this.openInNewTab(this.companyInfoURL);
  }

  openInNewTab(href) {
    Object.assign(document.createElement('a'), {
      target: '_blank',
      href,
    }).click();
  }

  impersonateCustomer() {
    this.showImpersonateDialog = true;
  }

  onImpersonateClose() {
    this.showImpersonateDialog = false;
  }
}
